import { Component, EventEmitter, Input, Output, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Cell } from './classes/cell';
import { Header } from './classes/header';
import { Utils } from '../../services/utils.service';
import { SessionService } from '../../services/session.service';
import { SubscriptionLike, Subscription, Observable } from 'rxjs';

import { SHARED_MODULES } from '../../../shared.';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PhDatepickerComponent } from '../ph-datepicker/ph-datepicker/ph-datepicker.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';

interface TableData {
	data: any[],
	columns: any[]
}

interface Table {
	data: { id: number, data: Cell[], checked?: boolean, checkedShadow?: boolean, disabled?: boolean }[],
	columns: Header[]
}

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
	imports: [
		SHARED_MODULES,
		GetLinkPipe,
		SanitizerPipe,
		AutocompleteComponent,
		FaIconComponent,
		PhDatepickerComponent,
		PhIconComponent
	],
    standalone: true
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy {
	tableData: TableData;
	table: Table = {
		data: [],
		columns: []
	};
	displayData: Table = {
		data: [],
		columns: []
	};

	paginateBtns: any[] = [];
	paginateBy: number = 10;
	page: number = 1;
	maxPage: number = 1;
	allSelected: boolean = false;
	selectingBtns: any[] = [];
	translateSubscription: SubscriptionLike;
	translateSubscription2: SubscriptionLike;
	selectedList: any[] = [];
	clickedList: any[] = [];
	sortByStatus: boolean = false;
	dataGrouped: boolean = false;
	groups_to_expand: string[] = [];
	dataType: string = '';
	itemDeleted: boolean = false;
	selectedItems: any[] = [];
	paginateToLastSubscription: Subscription;

	@Input() selecting: boolean = false;
	@Input() tableBorders: boolean = false;
	@Input() showSelectedCount: boolean = true;
	@Input() fixedHeader: boolean = false;
	@Input() defaultSortByExecutionDate: boolean = false;
	@Input() defaultSortByName: boolean = false;
	@Input() sortInMultiExecution: boolean = false;
	@Input() disabledRowTooltip: string = '';
	@Input('tableHeight') tableHeight: boolean = false;
	@Input('tableIdx') tableIdx: number;
	@Input('isShareLayout') isShareLayout: boolean = false;
	@Input('date-format') date_format: string;
	@Input() paginateToLast: Observable<void>;
	@Input('sortby') sortby: string;
	@Input('order') order: string;
	@Output('select') selectEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('selected') selectedEmmiter: EventEmitter<any> = new EventEmitter<any>();
	@Output('clicked') clickedEmmiter: EventEmitter<any> = new EventEmitter<any>();
	@Output('dblclicked') dblClickedEmmiter: EventEmitter<any> = new EventEmitter<any>();
	@Output('unselected') unselectedEmmiter: EventEmitter<any> = new EventEmitter<any>();
	@Output('selectedAll') selectedAllEmmiter: EventEmitter<any> = new EventEmitter<any>();
	@Output('sorted') sorted: EventEmitter<any> = new EventEmitter<any>();
	@Output('paginate') paginate_e: EventEmitter<any> = new EventEmitter<any>();
	@Output('sortedList') sortedList: EventEmitter<any> = new EventEmitter<any>();
	@Output('selectedManager') selectedManagerEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('selectedScheduledDate') selectedScheduledDateEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('selectedDeadline') selectedDeadlineEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('changeToggle') changeToggleEmitter: EventEmitter<any> = new EventEmitter<any>();

	constructor(private translate: TranslateService, public session: SessionService) {
	}

	@Input('page') set setPage(value) {
		this.page = value || 1;
	}

	@Input('sortTableByStatus') set setSortByStatus(value: boolean) {
		this.sortByStatus = value;
	}

	@Input('dataType') set setDataType(value: string) {
		this.dataType = value;
	}

	@Input('selectedItems') set setSelectedItems(value: any[]) {
		this.selectedItems = value;
	}

	@Input('table-data') set updateTableData(table: TableData) {
		this.tableData = table;
		this.initTable();
	}

	@Input('buttons') set selectingButtons(btns: any[]) {
		this.selectingBtns = btns;
		const keys = btns.map(btn => btn.title);

		if (!keys.length) return;

		if (this.translateSubscription) {
			this.translateSubscription.unsubscribe();
		}

		this.translateSubscription = this.translate.stream(keys).subscribe(translateDict => {
			this.selectingBtns.forEach(btn => {
				btn.html = (btn.disabled ? btn.disabledTemplate : btn.template).replace('<btnName>', translateDict[btn.title]);
			});
		});
	}

	@Input('dataGrouped') set setDataGrouped(value) {
		this.dataGrouped = value;

		this.selectAllSet();
		this.setDisplayData();
	}

	@Input('table_sorting') set setTableSorting(table_sorting: { index: number, order: string, tableIdx: number }) {
		this.table.columns.forEach((th) => th.clearSorting());

		if (table_sorting && table_sorting.tableIdx == this.tableIdx) {
			this.displayData.columns[table_sorting.index].sorting = true;
			this.displayData.columns[table_sorting.index].order =
				table_sorting.order == 'asc' ? 'desc' : 'asc';

			this.table.data = this.sortInMultiExecution
				? this.table.data.sort(
					this.sortData(this.displayData.columns[table_sorting.index])
				)
				: Utils.arrayDivision(
					this.table.data,
					this.displayData.columns[table_sorting.index].order,
					table_sorting.index
				);

			this.setDisplayData();
		}
	}

	get selectedCount(): number {
		return this.selectedList.length;
	}

	get disabledAll(): boolean {
		return this.table.data.every(item => item.disabled);
	}

	static isGroupedData(data = []) {
		return (data[0] || {}).hasOwnProperty('children');
	}

	static groupDataByParent(data: any[], parents: any[]) {
		const output = {};

		data.forEach(i => {
			const parent_index = parents.findIndex(p => p.data[0].displayValue == i.data[0].parent_id[0]);

			if (!output[parent_index]) {
				output[parent_index] = [i];
				return;
			}

			output[parent_index].push(i);
		});

		return output;
	}

	sortData(th) {
		const index = this.displayData.columns.indexOf(th),
			order = th.changedOrder,
			thDisplayValue = th.displayValue.toLowerCase();

		return (a, b) => {
			if (a.disabled == false || b.disabled == false || a.disabled == true || b.disabled == true) {
				const aDisabled = a.disabled,
					bDisabled = b.disabled;
				return Utils.sortPlanogramData({
					firstEl: a.data[index].sortingValue,
					secondEl: b.data[index].sortingValue,
					order: order,
					displayValue: thDisplayValue,
					dateFormat: this.date_format,
					disabledFirstEl: aDisabled,
					disabledSecondEl: bDisabled
				});
			}

			return Utils.generalSorting({
				firstEl: a.data[index].sortingValue,
				secondEl: b.data[index].sortingValue,
				order: order,
				displayValue: thDisplayValue
			});
		};
	}

	sortGroupedData(th) {
		const index = this.displayData.columns.indexOf(th),
			order = th.changedOrder,
			thDisplayValue = th.displayValue.toLowerCase();

		return (a, b) => {
			return Utils.generalSorting({
				firstEl: a.parent[index],
				secondEl: b.parent[index],
				order: order,
				displayValue: thDisplayValue
			});
		};
	}

	getGroupChildrenRows(groupKey) {
		const group = this.tableData.data.find(g => g.parent[0] === groupKey);
		if (!group) return [];
		return this.table.data.filter(row => group.children.some(c => c['id'] === row['id']));
	}

	updateParentState(parentTr, groupKey) {
		const childrenRows = this.getGroupChildrenRows(groupKey);
		const enabled = childrenRows.filter(c => !c.disabled);
		const allChecked = enabled.length > 0 && enabled.every(c => c.checked);
		const someChecked = enabled.some(c => c.checked);
		parentTr.checked = allChecked;
		parentTr.checkedShadow = !allChecked && someChecked;
	}

	selectHandler(tr, event, idx?, isParent: boolean = false) {
		tr.checked = event;

		if (isParent) {
			const groupKey = tr.data[0].value;
			const childrenRows = this.getGroupChildrenRows(groupKey);
			childrenRows.forEach(child => {
				if (child.disabled) return;
				child.checked = event;
				if (event) {
					if (!this.selectedList.some(s => s.id === child.id)) this.selectedList.push(child);
					this.selectedEmmiter.emit(child);
				} else {
					this.selectedList = this.selectedList.filter(s => s.id !== child.id);
					this.unselectedEmmiter.emit(child);
				}
			});
			this.updateParentState(tr, groupKey);
			if (event) this.selectedEmmiter.emit(tr);
			else this.unselectedEmmiter.emit(tr);
			this.setDisplayData();
		} else {
			if (event) {
				if (!this.selectedList.some(s => s.id === tr.id)) this.selectedList.push(tr);
				this.selectedEmmiter.emit(tr);
			} else {
				this.unselectedEmmiter.emit(tr);
				this.selectedList = this.selectedList.filter(i => i.id !== tr.id);
			}
			const tableRow = this.table.data.find(r => r.id === tr.id);
			if (tableRow) tableRow.checked = event;
			if (this.dataGrouped && tr.data[0].parent_id) {
				const groupKey = tr.data[0].parent_id[0];
				const parentTr = this.displayData.data.find(p => p.data[0].parent && p.data[0].value === groupKey);
				if (parentTr) this.updateParentState(parentTr, groupKey);
				// this.setDisplayData();
			}
		}

		this.selectAllSet();
	}

	clickEvent(tr, $event) {
		$event.stopPropagation();
		const obj = {
			tr: tr,
			event: $event
		};
		this.clickedList.push(obj);
		this.clickedEmmiter.emit(obj);
	}

	dblClickEvent(tr, event, td) {
		if (td.sortingValue != tr.data[0].sortingValue) return;
		const obj = {
			tr: tr,
			event: event
		};

		this.dblClickedEmmiter.emit(obj);
	}

	selectedAction(key, event?) {
		const data = this.table.data.filter(row => row.checked);
		this.selectEmitter.emit({ data, key, event });
	}

	ngOnInit() {
		this.groups_to_expand = [];

		if (this.defaultSortByExecutionDate) {
			let executionDateColumn = this.displayData.columns.findIndex(col => 'EXECUTE_DATE' == col.value);
			if (executionDateColumn == -1) executionDateColumn = this.displayData.columns.findIndex(col => 'EXECUTION_DATE' == col.value);
			if (executionDateColumn != -1) {
				this.displayData.columns[executionDateColumn].order = 'asc';
				this.sort(this.displayData.columns[executionDateColumn]);
			}
		}

		if (this.defaultSortByName) {
			this.displayData.columns[0].order = 'desc';
			this.sort(this.displayData.columns[0]);
		}

		if (this.sortby) {
			const sortedColumn = this.displayData.columns.find(col => this.sortby == col.value);

			if (!sortedColumn) return;

			sortedColumn.order = this.order || 'asc';
			this.sort(sortedColumn);
		}
	}

	ngAfterViewInit(): void {
		if (this.paginateToLast) {
			this.paginateToLastSubscription = this.paginateToLast.subscribe(() => {
				const data = this.getData();
				this.maxPage = Math.ceil(data.length / this.paginateBy);
				if (this.page > this.maxPage)
					this.paginate(this.maxPage || 1);
			});
		}
	}

	ngOnDestroy() {
		if (this.translateSubscription) {
			this.translateSubscription.unsubscribe();
		}
		if (this.translateSubscription2) {
			this.translateSubscription2.unsubscribe();
		}
		if (this.paginateToLastSubscription) {
			this.paginateToLastSubscription.unsubscribe();
		}

		this.translateSubscription = null;
		this.translateSubscription2 = null;
		this.paginateToLastSubscription = null;
	}

	initTable() {
		this.table.columns = this.tableData.columns.map(column => new Header(column));

		if (!TableComponent.isGroupedData(this.tableData.data)) {
			this.table.data = this.tableData.data.map(row => {
				const rowChecked = this.selectedList.length ?
					this.selectedList.find(item => {
						if (item.main_stand_id && item.planogram_id) {
							return item.main_stand_id == row.main_stand_id && item.planogram_id == row.planogram_id;
						}
						return item.data[0].displayValue == row[0];
					}) :
					false;

				const checked = row.checked === false ? false : (row.checked || !!rowChecked);

				return {
					checked: checked,
					active: row.active,
					is_new: row.is_new,
					disabled: row.disabled,
					data: row.map(cell => new Cell(cell)),
					main_stand_id: row.main_stand_id,
					planogram_id: row.planogram_id,
					id: row.id
				};
			});
		} else {
			this.table.data = this.tableData.data.flatMap(group => group.children.map(child => {
				const rowChecked = this.selectedList.length ?
					this.selectedList.find(item => {
						if (item.main_stand_id && item.planogram_id) {
							return item.main_stand_id == child.main_stand_id && item.planogram_id == child.planogram_id;
						}
						return item.data[0].displayValue == child[0];
					}) :
					false;

				const checked = child.checked === false ? false : (child.checked || !!rowChecked);

				return {
					checked: checked,
					active: child.active,
					is_new: child.is_new,
					disabled: child.disabled,
					data: child.map(cell => new Cell(cell)),
					main_stand_id: child.main_stand_id,
					planogram_id: child.planogram_id,
					id: child.id
				};
			}));
		}

		this.displayData.columns = this.table.columns;

		const allUnchecked = this.table.data.length > 0 && this.table.data.every(row => !row.checked);
		if (allUnchecked) {
			this.selectedList = [];
		}

		this.setDisplayData();

		let keys = [];

		if (!TableComponent.isGroupedData(this.tableData.data)) {
			this.tableData.data.forEach(row => {
				row.forEach(cell => {
					if (cell && Array.isArray(cell.translates)) {
						keys = keys.concat(cell.translates);
					}
				});
			});
		}

		keys = keys.reduce((x, y) => x.includes(y) ? x : [...x, y], []);

		if (!keys.length) return;
		if (this.translateSubscription2) this.translateSubscription2.unsubscribe();

		this.translateSubscription2 = this.translate.stream(keys).subscribe(translateDict => {
			if (!TableComponent.isGroupedData(this.tableData.data)) {
				this.table.data.forEach(row => {
					row.data.forEach(cell => {
						cell.translates = translateDict;
						cell.setDisplayValue();
					});
				});
			}
		});
	}

	setDisplayData(paginate: boolean = false) {
		this.table.data.map(item => {
			if (this.getType(item.data[0].value) == 'link') {
				item.data[0].displayValue = item.data[0].value.name;
				item.data[0].sortingValue = item.data[0].value.name;
			}
			if (item.data.length > 4 && this.getType(item.data[4].value) == 'select') {
				if (item.data[4].value.data.managers && item.data[4].value.data.managers.length) {
					let index = item.data[4].value.data.managers.findIndex(manager => manager.active);
					if (index < 0) index = 0;
					item.data[4].displayValue = item.data[4].value.data.managers[index].name;
					item.data[4].sortingValue = item.data[4].value.data.managers[index].name;
				}
			}
			const pickerIndex = item.data.findIndex(data => this.getType(data.value) == 'datepicker'),
				dateIndex = item.data.findIndex(data => this.getType(data.value) == 'deadline_date');
			if (pickerIndex !== -1) {
				const value = item.data[pickerIndex].value.data.date ? new Date(item.data[pickerIndex].value.data.date).getTime() : 0;
				item.data[pickerIndex].displayValue = value;
				item.data[pickerIndex].sortingValue = value;
			}
			if (dateIndex !== -1) {
				const value = item.data[dateIndex].value.data.deadline_date ? new Date(item.data[dateIndex].value.data.deadline_date).getTime() : 0;
				item.data[dateIndex].displayValue = value;
				item.data[dateIndex].sortingValue = value;
			}

			const toggleIndex = item.data.findIndex(data => this.getType(data.value) == 'toggle'),
				timeTrackerIndex = item.data.findIndex(data => this.getType(data.value) == 'timeTracker');
			if (toggleIndex !== -1) {
				const value = item.data[toggleIndex].value.data.value;
				item.data[toggleIndex].displayValue = value;
				item.data[toggleIndex].sortingValue = value;
			}
			if (timeTrackerIndex !== -1) {
				const value = item.data[timeTrackerIndex].value.data.time ?
					new Date('01.' + item.data[timeTrackerIndex].value.data.time).getTime() :
					item.data[timeTrackerIndex].value.data.enabled ? 1 : 0;
				item.data[timeTrackerIndex].displayValue = value;
				item.data[timeTrackerIndex].sortingValue = value;
			}
		});
		let from = (this.page - 1) * this.paginateBy,
			to = from + this.paginateBy,
			selectedChildren = this.selectedList.map(c => c.data[0].value);

		if (TableComponent.isGroupedData(this.tableData.data)) {
			const th = this.displayData.columns.find(s => s.sorting);

			if (th) {
				const data = ((this.tableData.data as any[])
						.sort(this.sortGroupedData(th))
						.map(d => {
							d.parent.parent = true;

							d.children.forEach(c => {
								c.hidden = !this.groups_to_expand.includes(d.parent[0]);
								c.parent_id = d.parent;
							});

							return [d.parent, ...d.children];
						}) as any)
						.flat()
						.filter(row => !row.hidden)
						.map((row: any) => ({
							checked: selectedChildren.includes(row[0]),
							checkedShadow: false,
							id: row['id'],
							data: row.map(cell => {
								const newCell = new Cell(cell);
								newCell.parent = row['parent'];
								newCell.hidden = row['hidden'];
								newCell.active = row['active'];
								newCell.is_new = row['is_new'];
								newCell.parent_id = row['parent_id'];
								return newCell;
							})
						}))
						.slice(from, to),

					parents = data.filter(row => row.data[0].parent),
					children = data.filter(row => !row.data[0].parent),
					output = [];

				const grouped_data = TableComponent.groupDataByParent(children, parents);

				for (let idx in grouped_data) {
					grouped_data[idx].sort(this.sortData(th));
				}

				if (grouped_data[-1]) {
					Array.prototype.push.apply(output, grouped_data[-1]);
				}

				parents.forEach((p, idx) => {
					output.push(p);

					if (grouped_data[idx]) {
						Array.prototype.push.apply(output, grouped_data[idx]);
					}
				});

				this.displayData.data = output;
			} else {
				this.displayData.data = ((this.tableData.data as any[])
					.map(d => {
						d.parent.parent = true;
						d.children.forEach(c => {
							c.hidden = !this.groups_to_expand.includes(d.parent[0]);
							c.parent_id = d.parent;
						});
						return [d.parent, ...d.children];
					}) as any)
					.flat()
					.filter(row => !row.hidden)
					.slice(from, to)
					.map((row: any) => {
						return {
							checked: selectedChildren.includes(row[0]),
							checkedShadow: false,
							id: row['id'],
							data: row.map(cell => {
								const newCell = new Cell(cell);
								newCell.parent = row['parent'] || false;
								newCell.hidden = row['hidden'] || false;
								newCell.active = row['active'];
								newCell.is_new = row['is_new'];
								newCell.parent_id = row['parent_id'];
								return newCell;
							})
						};
					});
			}
			if (this.dataGrouped) {
				this.displayData.data.filter(tr => tr.data[0].parent).forEach(parentTr => {
					const groupKey = parentTr.data[0].value;
					this.updateParentState(parentTr, groupKey);
				});
			}
		} else {
			if (from > this.table.data.length) {
				this.page = 1;
				from = 0;
				to = from + this.paginateBy;
			}
			this.displayData.data = this.table.data.slice(from, to);
		}

		this.setSelectedRow();
		this.setPaginateButtons();
		this.selectAllSet(paginate);
	}

	getData() {
		return ((this.tableData.data as any[])
			.map(d => {
				d.parent.parent = true;
				d.children.forEach(c => {
					c.hidden = !this.groups_to_expand.includes(d.parent[0]);
				});
				return [d.parent, ...d.children];
			}) as any)
			.flat()
			.filter(row => !row.hidden);
	}

	setPaginateButtons() {
		let btns = [];

		if (TableComponent.isGroupedData(this.tableData.data)) {
			const data = this.getData();

			this.maxPage = Math.ceil(data.length / this.paginateBy);
		} else {
			this.maxPage = Math.ceil(this.table.data.length / this.paginateBy);
		}
		if (this.maxPage <= 5) {
			for (let i = 1; i <= this.maxPage; i++) {
				btns.push(i);
			}

			this.paginateBtns = btns;
			return;
		}

		if (this.page < 5) {
			for (let i = 1; i <= 5; i++) {
				btns.push(i);
			}

			btns.push('...');
			btns.push(this.maxPage);
			this.paginateBtns = btns;

			return;
		}
		if (this.maxPage - this.page < 4) {
			for (let i = this.maxPage; i >= this.maxPage - 4; i--) {
				btns.push(i);
			}

			btns.push('...');
			btns.push(1);
			btns.reverse();

			this.paginateBtns = btns;
			return;
		}

		this.paginateBtns = [1, '...', this.page - 1, this.page, this.page + 1, '...', this.maxPage];
	}

	paginateCount(value) {
		this.paginateBy = parseInt(value);
		this.page = 1;
		this.setDisplayData();
	}

	paginate(value) {
		if (typeof value != 'number') return;
		this.paginate_e.emit(value);
		this.page = value;
		this.setDisplayData(true);
	}

	sort(th: Header) {
		const order = th.changedOrder,
			index = this.displayData.columns.indexOf(th);

		this.table.columns.forEach(th => th.clearSorting());

		th.sorting = true;
		th.order = order;

		this.table.data = this.sortInMultiExecution
			? this.table.data.sort(this.sortData(th))
			: Utils.arrayDivision(this.table.data, th.order, index);
		this.tableData.data.sort(
			Utils.sortData(
				this.tableData.columns.indexOf(th.value),
				th.order == 'asc' ? 'desc' : 'asc'
			)
		);
		this.setDisplayData();

		const sortedEmit = {
			index: this.displayData.columns.indexOf(th),
			order: th.changedOrder,
			tableIdx: this.tableIdx
		};

		setTimeout(() => {
			this.sorted.emit(sortedEmit);
			this.sortedList.emit(this.tableData.data);
		});
	}

	selectAllChange(value) {
		this.allSelected = value;
		const rows = this.table.data.filter(r => !r.disabled);
		rows.forEach(r => r.checked = value);
		const rowIds = new Set(rows.map(r => r.id));
		if (value) {
			this.selectedList = [...this.selectedList.filter(s => !rowIds.has(s.id)), ...rows];
		} else {
			this.selectedList = this.selectedList.filter(s => !rowIds.has(s.id));
		}
		this.setDisplayData();
		this.selectedAllEmmiter.emit(value);
	}

	selectAllSet(from_paginate: boolean = false) {
		const prev_value = this.allSelected;
		const rows = this.table.data.filter(r => !r.disabled);
		this.allSelected = rows.length > 0 && rows.every(r => r.checked);
		if (prev_value && from_paginate && !this.allSelected) {
			this.allSelected = prev_value;
		}
	}

	toggleChildrenVisibility(tr) {
		if (!tr.data[0].parent) return;

		const name = tr.data[0].value,
			group_exist = this.groups_to_expand.find(g => g == name);

		if (group_exist) {
			this.groups_to_expand = this.groups_to_expand.filter(g => g != name);
		} else {
			this.groups_to_expand.push(name);
		}

		this.setDisplayData();
	}

	setSelectedRow() {
		if (this.itemDeleted) {
			this.itemDeleted = false;
			this.selectedList = [];

			return;
		}

		if (!this.selectedList.length && this.table.data.some(el => el.checked)) {
			this.selectedList = this.table.data.filter(el => el.checked);
			return;
		}

		if (this.selectedItems.length) {
			this.selectedList.forEach(row =>
				row.checked = !!this.selectedItems.find(el => el.stand__main_stand__id == row.id && el.planogram_id == row.planogram_id)
			);

			return;
		}
		this.selectedList.map(row =>
			this.table.data.find(el => {
				if ((el['id'] || el.data[0].value.planogram_id) === (row['id'] || row.data[0].value.planogram_id && !row.disabled)) {
					return row.checked = el.checked;
				}
			})
		);
	}

	selectedManager(event, row) {
		this.selectedManagerEmitter.emit({
			manager: event,
			planogram: row.data[0].value.planogram_id,
			stand: row.data[0].value.stand_id
		});
	}

	onDatePickerSetDate(event: any, row: any, cell: any): void {
		if (cell.value.data.type === 'scheduled') {
			this.selectedScheduledDateEmitter.emit({
				date: event,
				planogram: row.data[0].value.planogram_id || row.planogram_id,
				stand: row.data[0].value.stand_id || row.id,
				main_stand: row.main_stand_id
			});
		} else if (cell.value.data.type === 'deadline') {
			this.selectedDeadlineEmitter.emit({
				date: event,
				planogram: row.data[0].value.planogram_id || row.planogram_id,
				stand: row.data[0].value.stand_id || row.id,
				main_stand: row.main_stand_id
			});
		}

		if (!row.checked) {
			this.selectHandler(row, event);
		}
	}

	changeToggle(value: boolean, row): void {
		const index = row.data.findIndex(data => data.value && data.value.data && data.value.data.hasOwnProperty('toggle'));
		if (index !== -1) row.data[index].value.data.value = value;

		this.changeToggleEmitter.emit({
			value: value,
			planogram: row.data[0].value.planogram_id || row.planogram_id,
			stand: row.data[0].value.stand_id || row.id,
			main_stand: row.main_stand_id
		});

		if (!row.checked) this.selectHandler(row, true);
	}

	getType(value) {
		switch (true) {
			case (typeof value == 'string'):
				return 'string';

			case (typeof value == 'object' && value && value.link):
				return 'link';

			case (typeof value == 'object' && value.data && value.data.select):
				return 'select';

			case (typeof value == 'object' && value.data && value.data.datepicker):
				return 'datepicker';

			case(typeof value == 'object' && value.data && value.data.hasOwnProperty('deadline_date')):
				return 'deadline_date';

			case(typeof value == 'object' && value.data && value.data.hasOwnProperty('toggle')):
				return 'toggle';

			case(typeof value == 'object' && value.data && value.data.hasOwnProperty('timeTracker')):
				return 'timeTracker';

			default:
				return 'default';
		}
	}
}
